import React, { useEffect } from 'react';
import { useLocalStorage } from 'react-use';
import { utcToZonedTime } from 'date-fns-tz';
import { logError } from 'commons/logger';

import scrollIntoViewUtil from 'commons/scrollIntoViewUtil';
import {
  PDP_DECIDELATER_TAB_ID,
  IATA_LOS_ANGELES_TIMEZONE,
} from 'commons/constants';
import useProductEFGInternalStatus from 'data-hooks/useProductEFGInternalStatus';
import useSetPdpSelectedCustomizerTab from 'components/consumer/PDP/state/pdpSelectedCustomizerTab/useSetPdpSelectedCustomizerTab';
import usePDPPrice from 'data-hooks/usePDPPrice';
import PDPDecideLaterPromoUI from './PDPDecideLaterPromoUI';
import usePDPOptions from '../state/context/usePDPOptions';

const PDPDecideLaterPromo = () => {
  // CON-1940 - If the user closes the widget by clicking the x, it should not reappear
  // on the page or on any page thereafter. The widget should only reappear once the next sale begins
  const KEY = 'PDPDecideLaterPromo';

  const { loading, data: priceData } = usePDPPrice();

  const {
    data: { upholsteryOptionId, hasSingleOptionType } = {},
  } = usePDPOptions();

  const { isEFGInternal } = useProductEFGInternalStatus();
  const [storedLocalStorageObj, setLocalStorageObj] = useLocalStorage(KEY);

  const setPdpSelectedCustomizerTab = useSetPdpSelectedCustomizerTab();

  useEffect(() => {
    if (
      !priceData ||
      !(priceData.promotion?.discount > 0) ||
      (storedLocalStorageObj &&
        storedLocalStorageObj.endDate === priceData.promotion?.endDate)
    ) {
      return;
    }
    if (!storedLocalStorageObj) {
      setLocalStorageObj({ hide: false, endDate: priceData.promotion.endDate });
      return;
    }

    const copyStoredLocalStorageObj = { ...storedLocalStorageObj };
    copyStoredLocalStorageObj.endDate = priceData.promotion.endDate;
    copyStoredLocalStorageObj.hide = false;
    setLocalStorageObj(copyStoredLocalStorageObj);
  }, [storedLocalStorageObj, priceData, setLocalStorageObj]);

  if (
    isEFGInternal ||
    hasSingleOptionType ||
    !upholsteryOptionId ||
    !priceData ||
    !(priceData.promotion?.discount > 0) ||
    storedLocalStorageObj?.hide === true
  ) {
    return null;
  }

  const minPromotion = priceData.promotion.discount;

  const isTieredPromotion = priceData.promotion.tiers?.length > 1;

  const maxTierPromotion = isTieredPromotion
    ? priceData.promotion.tiers.reduce((maxDisc, tier) => {
        if (tier.discount > maxDisc) {
          return tier.discount;
        }
        return maxDisc;
      }, 0)
    : null;

  const buttonText = maxTierPromotion
    ? `Lock in up to ${maxTierPromotion}% off now`
    : `Lock in ${minPromotion}% off now`;
  let zonedEndDate = null;
  try {
    zonedEndDate = utcToZonedTime(
      priceData.promotion.endDate,
      IATA_LOS_ANGELES_TIMEZONE
    );
  } catch (e) {
    logError(e);
  }
  const formattedEndDate = zonedEndDate
    ? `${zonedEndDate.getMonth() + 1}/${zonedEndDate.getDate()}`
    : null;

  return formattedEndDate ? (
    <PDPDecideLaterPromoUI
      loading={loading}
      heading="Buy Now, Decide Later"
      subheading={`(Sale ends ${formattedEndDate})`}
      buttonText={buttonText}
      onButtonClick={() => {
        setPdpSelectedCustomizerTab(PDP_DECIDELATER_TAB_ID);
        scrollIntoViewUtil('[data-xc="PDPDesktop"]');
      }}
      onClose={() => {
        setLocalStorageObj({
          hide: true,
          endDate: priceData.promotion.endDate,
        });
      }}
    />
  ) : null;
};

export default PDPDecideLaterPromo;
